import { CellValueChangedEvent, GridApi } from 'ag-grid-community';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLoadingBarState } from '../../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { bulkUpdateReportingStatus } from '../../../../../services/queries/MaggieCompanyQueries';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { LoadingId } from '../../../../../types';
import { getForesightStore } from '../../../../../util/jotai-store';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';
import { IKpiCompanyData } from '../AllCompaniesData';

const DEBOUNCE_MS = 800;

/**
 * Debounced auto-save for the reporting status toggle in the AllCompanies grid.
 *
 * Listens to `onCellValueChanged` for the `reporting` field, accumulates changes,
 * and flushes them via `PUT /company/bulk-reporting` after a debounce window.
 *
 * Key guarantees:
 * - Toggle is never disabled -- `setDataValue` provides instant optimistic feedback.
 * - Latest value always wins -- `Map` deduplicates to the most recent toggle per company.
 * - Serialized flushes -- only one API batch in-flight at a time; re-edited companies
 *   during a flush are deferred to the next batch.
 * - Rollback on failure -- grid reverts to original values for failed companies.
 */
export function useReportingStatusAutoSave(gridApi: GridApi | null) {
  const { pushErrorToast } = useToastMessageState();
  const { actions } = useLoadingBarState();
  const invalidateKpiRequests = useInvalidateKPIRequests();

  const pendingRef = useRef(new Map<number, boolean>());
  const originalsRef = useRef(new Map<number, boolean>());
  const isFlushingRef = useRef(false);
  const isRollingBackRef = useRef(false);
  const gridApiRef = useRef(gridApi);
  gridApiRef.current = gridApi;

  const flush = useCallback(async () => {
    if (isFlushingRef.current) {
      return;
    }
    isFlushingRef.current = true;

    const pending = new Map(pendingRef.current);
    const originals = new Map(originalsRef.current);
    pendingRef.current.clear();
    originalsRef.current.clear();

    const toEnable = [...pending].filter(([, v]) => v).map(([id]) => id);
    const toDisable = [...pending].filter(([, v]) => !v).map(([id]) => id);

    actions.startLoading(LoadingId.bulkUpdateReportingStatus);
    try {
      const [enableResult, disableResult] = await Promise.allSettled([
        toEnable.length > 0 ? bulkUpdateReportingStatus(toEnable, true) : null,
        toDisable.length > 0 ? bulkUpdateReportingStatus(toDisable, false) : null,
      ]);

      const store = getForesightStore();
      let hasSuccess = false;

      for (const [result, , value] of [
        [enableResult, toEnable, true],
        [disableResult, toDisable, false],
      ] as const) {
        if (!result || result.status !== 'fulfilled' || !result.value) {
          continue;
        }
        const { processed, errors } = result.value;

        if (processed?.length) {
          hasSuccess = true;
          for (const { id } of processed) {
            if (pendingRef.current.has(id)) {
              continue;
            }
            store.set(companyStateJ(id), (prev) => (prev ? { ...prev, reporting: value } : prev));
          }
        }

        if (errors?.length) {
          rollbackCompanies(
            originals,
            errors.map((e) => e.companyId)
          );
          const names = errors.map((e) => e.name).join(', ');
          pushErrorToast({ message: `Failed to update reporting status for ${names}` });
        }
      }

      if (hasSuccess) {
        invalidateKpiRequests();
      }
    } catch (error) {
      console.error(error);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to update reporting status') });
      rollbackCompanies(originals, [...pending.keys()]);
    } finally {
      actions.stopLoading(LoadingId.bulkUpdateReportingStatus);
      isFlushingRef.current = false;
    }

    if (pendingRef.current.size > 0) {
      flush();
    }
  }, [actions, invalidateKpiRequests, pushErrorToast]);

  function rollbackCompanies(originals: Map<number, boolean>, companyIds: number[]) {
    isRollingBackRef.current = true;
    try {
      for (const id of companyIds) {
        const original = originals.get(id);
        if (original === undefined) {
          continue;
        }
        gridApiRef.current?.getRowNode(String(id))?.setDataValue('reporting', original);
      }
    } finally {
      isRollingBackRef.current = false;
    }
  }

  const debouncedFlush = useMemo(() => debounce(flush, DEBOUNCE_MS), [flush]);

  useEffect(() => {
    return () => {
      debouncedFlush.flush();
    };
  }, [debouncedFlush]);

  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<IKpiCompanyData>) => {
      if (isRollingBackRef.current) {
        return;
      }
      if (event.colDef.field !== 'reporting') {
        return;
      }
      const companyId = event.data?.id;
      if (companyId == null) {
        return;
      }

      if (!originalsRef.current.has(companyId)) {
        originalsRef.current.set(companyId, event.oldValue as boolean);
      }
      pendingRef.current.set(companyId, event.newValue as boolean);
      debouncedFlush();
    },
    [debouncedFlush]
  );

  return { onCellValueChanged };
}
